import { AnyAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { inventoriesClient, itemsClient } from '../../../api/client';
import { getPagination, keysToId } from '../../../helpers/sliceHelper';
import { RootState } from '../../store';
import {
  FoundItemsType,
  FoundListType,
  InventoriesType,
  InventoryDetail,
  InventoryDetailType,
  InventoryListQuery,
  InventoryListType,
  InventoryState,
} from './inventoryInterface';

const listsAdapter = createEntityAdapter<InventoryListType>();
const detailsAdapter = createEntityAdapter<InventoryDetailType>();
const foundAdapter = createEntityAdapter<FoundListType>();

export const listInventories = createAsyncThunk('inventory/listInventories', async (query: InventoryListQuery) =>
  inventoriesClient.inventoryList(query.siteId, query.from, query.to, query.offset, query.limit),
);

export const getInventory = createAsyncThunk('inventory/getInventory', async (id: string) => inventoriesClient.inventoryDetail(id));

// Lost items a scan saw again (the Found list), with the same site/date filters as the inventories.
export const listFoundItems = createAsyncThunk('inventory/listFoundItems', async (query: InventoryListQuery) =>
  itemsClient.itemsFound(query.from, query.to, query.siteId, query.offset, query.limit),
);

export const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    lists: listsAdapter.getInitialState(),
    details: detailsAdapter.getInitialState(),
    found: foundAdapter.getInitialState(),
  } as InventoryState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(listFoundItems.pending, (state: InventoryState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listFoundItems: true };
    });
    builder.addCase(listFoundItems.fulfilled, (state: InventoryState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listFoundItems: false };
      if (action.payload.data?.result) {
        foundAdapter.upsertOne(state.found, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listFoundItems.rejected, (state: InventoryState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listFoundItems: false };
    });
    builder.addCase(listInventories.pending, (state: InventoryState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listInventories: true };
    });
    builder.addCase(listInventories.fulfilled, (state: InventoryState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listInventories: false };
      if (action.payload.data?.result) {
        listsAdapter.upsertOne(state.lists, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listInventories.rejected, (state: InventoryState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listInventories: false };
    });
    builder.addCase(getInventory.pending, (state: InventoryState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getInventory: true };
    });
    builder.addCase(getInventory.fulfilled, (state: InventoryState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getInventory: false };
      if (action.payload.data?.result) {
        detailsAdapter.upsertOne(state.details, { id: action.meta.arg, ...action.payload.data });
      }
    });
    builder.addCase(getInventory.rejected, (state: InventoryState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getInventory: false };
    });
  },
});

const { selectById: selectListById } = listsAdapter.getSelectors((state: RootState) => state.inventory.lists);
const { selectById: selectDetailById } = detailsAdapter.getSelectors((state: RootState) => state.inventory.details);
const { selectById: selectFoundById } = foundAdapter.getSelectors((state: RootState) => state.inventory.found);

const selectFound = createSelector([selectFoundById], (list) => ({
  total: list?.record_count ?? 0,
  result: list?.result,
}));

export const getFoundItems =
  (keys: InventoryListQuery) =>
  (state: RootState): FoundItemsType =>
    selectFound(state, keysToId(keys));

export const foundItemsFetching = (state: RootState): boolean => state.inventory.fetching?.listFoundItems !== false;

const selectInventories = createSelector([selectListById], (list) => ({
  pagination: getPagination(list),
  result: list?.result,
}));

export const getInventories =
  (keys: InventoryListQuery) =>
  (state: RootState): InventoriesType =>
    selectInventories(state, keysToId(keys));

export const getInventoryById =
  (id: string) =>
  (state: RootState): InventoryDetail | undefined =>
    selectDetailById(state, id)?.result;

export const inventoriesFetching = (state: RootState): boolean => state.inventory.fetching?.listInventories !== false;
export const inventoryFetching = (state: RootState): boolean => state.inventory.fetching?.getInventory !== false;

export default inventorySlice.reducer;
